// Field definitions for the Create Contract page. Names match the PDF form fields
// listed in fillContract.js — do not rename them.

const APN_HELP = "Find this on your county assessor's website";

const propertySection = (extra = []) => ({
  title: "Property",
  fields: [
    { name: "property_address_1", label: "Street address", placeholder: "1234 Main St" },
    { name: "property_address_2", label: "City, State ZIP", placeholder: "Albuquerque, NM 87102" },
    ...extra,
    { name: "apn", label: "APN (Assessor's Parcel Number)", placeholder: "101-205-334", help: APN_HELP },
  ],
});

const escrowSection = {
  title: "Escrow / title company",
  fields: [
    { name: "escrow_name", label: "Company name" },
    { name: "escrow_officer", label: "Escrow officer" },
    { name: "escrow_address", label: "Company address" },
    { name: "escrow_email", label: "Officer email", type: "email" },
  ],
};

const costSplits = (extra = []) => ({
  title: "Who pays what",
  fields: [
    {
      kind: "pickone",
      name: "escrow_fees",
      label: "Escrow fees",
      options: [
        { field: "escrow_fees_split", label: "Split 50/50" },
        { field: "escrow_fees_buyer", label: "Buyer pays" },
      ],
    },
    {
      kind: "pickone",
      name: "title_policy",
      label: "Owner's title policy",
      options: [
        { field: "title_policy_seller", label: "Seller pays" },
        { field: "title_policy_buyer", label: "Buyer pays" },
      ],
    },
    ...extra,
    {
      kind: "pickone",
      name: "hoa_fees",
      label: "HOA transfer fees",
      options: [
        { field: "hoa_fees_split", label: "Split 50/50" },
        { field: "hoa_fees_buyer", label: "Buyer pays" },
      ],
    },
  ],
});

const partiesSection = {
  title: "Parties",
  fields: [
    { name: "seller_names", label: "Seller name(s) as on title", placeholder: "John Doe and Mary Doe" },
    { name: "buyer_name", label: "Buyer name on the contract", placeholder: "Gallardo Luxe LLC and/or assigns" },
  ],
};

const signatureSection = {
  title: "Signature block",
  fields: [
    { name: "seller1_printed_name", label: "Seller 1 printed name" },
    { name: "seller1_phone", label: "Seller 1 phone" },
    { name: "seller1_email", label: "Seller 1 email", type: "email" },
    { name: "seller1_mailing_address", label: "Seller 1 mailing address" },
    { name: "seller2_printed_name", label: "Seller 2 printed name (if any)" },
    { name: "seller2_phone", label: "Seller 2 phone" },
    { name: "seller2_email", label: "Seller 2 email", type: "email" },
    { name: "seller2_mailing_address", label: "Seller 2 mailing address" },
    { name: "buyer_company_name", label: "Buyer company name" },
    { name: "buyer_signer_name", label: "Buyer signer name" },
    { name: "buyer_email", label: "Buyer email", type: "email" },
    { name: "buyer_phone", label: "Buyer phone" },
  ],
};

const moneyFields = (coeHint) => [
  { name: "earnest_money", label: "Earnest money deposit", kind: "number", placeholder: "1,000" },
  { name: "purchase_price", label: "Purchase price", kind: "number", placeholder: "181,423" },
  { name: "coe_date", label: "Close of escrow date", kind: "date", dateStyle: coeHint.includes(",") ? "long" : "monthday" },
];

export const DEAL_TYPES = [
  {
    id: "house",
    label: "House",
    blurb: "Standard cash purchase contract",
    template: "Purchase_Contract_Fillable.pdf",
    sections: [
      propertySection(),
      {
        title: "Terms",
        fields: [
          ...moneyFields("November 20"),
          { name: "inspection_days", label: "Inspection period (days)", kind: "number", placeholder: "10" },
        ],
      },
      escrowSection,
      partiesSection,
      costSplits(),
      {
        title: "Extras",
        fields: [
          { name: "section_1_11", label: "Personal property / rights included", kind: "textarea", placeholder: "Fridge, washer/dryer, all mineral rights" },
          { name: "section_1_12", label: "Additional terms", kind: "textarea" },
        ],
      },
      signatureSection,
    ],
  },
  {
    id: "land",
    label: "Land",
    blurb: "Vacant land purchase contract",
    template: "Land_Purchase_Contract_Fillable.pdf",
    sections: [
      propertySection([
        { name: "county_state", label: "County, State", placeholder: "Bernalillo County, New Mexico" },
        { name: "acreage", label: "Acreage", kind: "number", placeholder: "2.5" },
      ]),
      {
        title: "Terms",
        fields: [
          ...moneyFields("November 20"),
          { name: "inspection_days", label: "Due diligence period (days)", kind: "number", placeholder: "30" },
        ],
      },
      escrowSection,
      partiesSection,
      costSplits([
        {
          kind: "pickone",
          name: "survey",
          label: "Survey",
          options: [
            { field: "survey_seller", label: "Seller pays" },
            { field: "survey_buyer", label: "Buyer pays" },
            { field: "survey_not_required", label: "Not required" },
          ],
        },
      ]),
      {
        title: "Extras",
        fields: [
          { name: "section_1_11", label: "Personal property / rights included", kind: "textarea", placeholder: "All water, mineral and timber rights" },
          { name: "section_1_12", label: "Additional terms", kind: "textarea" },
        ],
      },
      signatureSection,
    ],
  },
  {
    id: "subto",
    label: "Mortgage Takeover",
    blurb: "Subject-to the existing loan",
    template: "Mortgage_Takeover_Contract.pdf",
    sections: [
      propertySection(),
      {
        title: "Terms",
        fields: [
          ...moneyFields("November 20, 2026"),
          { name: "inspection_days", label: "Inspection period (days)", kind: "number", placeholder: "10" },
        ],
      },
      {
        title: "Existing loan",
        fields: [
          { name: "loan_servicer", label: "Lender / servicer", placeholder: "Wells Fargo" },
          { name: "loan_number", label: "Loan number" },
          { name: "loan_balance", label: "Unpaid principal balance", kind: "number" },
          { name: "loan_payment", label: "Monthly payment (PITI)", kind: "number" },
          { name: "loan_rate", label: "Interest rate", kind: "number", placeholder: "3.5" },
          {
            kind: "pickone",
            name: "loan_rate_type",
            label: "Rate type",
            options: [
              { field: "loan_rate_fixed", label: "Fixed" },
              { field: "loan_rate_adjustable", label: "Adjustable" },
            ],
          },
          {
            kind: "pickone",
            name: "loan_status",
            label: "Loan status",
            options: [
              { field: "loan_status_current", label: "Current" },
              { field: "loan_status_behind", label: "Behind" },
            ],
          },
          { name: "loan_arrears", label: "Amount in arrears", kind: "number", showIf: (f) => f.loan_status === "loan_status_behind" },
          { name: "other_liens", label: "Other liens on title", kind: "textarea", placeholder: "None" },
        ],
      },
      {
        title: "How the seller gets paid",
        fields: [
          { name: "subto_amount", label: "Loan balance taken subject-to", kind: "number" },
          { name: "cash_to_seller", label: "Cash to seller at closing", kind: "number" },
          { name: "arrears_paid", label: "Arrears paid by buyer at closing", kind: "number" },
          { name: "equity_carry_amount", label: "Seller equity carried", kind: "number" },
          { name: "equity_carry_payment", label: "Equity carry monthly payment", kind: "number" },
          { name: "equity_carry_months", label: "Equity carry term (months)", kind: "number" },
          { name: "equity_carry_rate", label: "Equity carry interest rate", kind: "number" },
        ],
      },
      {
        title: "Loan payments & impounds",
        fields: [
          {
            kind: "pickone",
            name: "servicing",
            label: "How loan payments get made",
            options: [
              { field: "servicing_via_company", label: "Through a servicing company" },
              { field: "servicing_direct", label: "Buyer pays the lender directly" },
            ],
          },
          { name: "servicing_company", label: "Servicing company name", showIf: (f) => f.servicing === "servicing_via_company" },
          {
            kind: "pickone",
            name: "impound",
            label: "Existing impound / escrow account",
            options: [
              { field: "impound_to_buyer", label: "Transfers to buyer" },
              { field: "impound_reimbursed", label: "Seller gets reimbursed" },
            ],
          },
        ],
      },
      escrowSection,
      partiesSection,
      costSplits(),
      {
        title: "Extras",
        fields: [
          { name: "section_1_14", label: "Personal property included", kind: "textarea" },
          { name: "section_1_15", label: "Additional terms", kind: "textarea" },
        ],
      },
      signatureSection,
    ],
  },
  {
    id: "seller_finance",
    label: "Seller Finance",
    blurb: "Seller carries the note",
    template: "Seller_Finance_Contract.pdf",
    sections: [
      propertySection(),
      {
        title: "Terms",
        fields: [
          ...moneyFields("November 20, 2026"),
          { name: "inspection_days", label: "Inspection period (days)", kind: "number", placeholder: "10" },
        ],
      },
      {
        title: "Note terms",
        fields: [
          { name: "down_payment", label: "Down payment", kind: "number" },
          { name: "amount_financed", label: "Amount financed", kind: "number" },
          { name: "interest_rate", label: "Interest rate", kind: "number", placeholder: "6.5" },
          { name: "monthly_payment", label: "Monthly payment", kind: "number" },
          {
            kind: "pickone",
            name: "payment_type",
            label: "Payment type",
            options: [
              { field: "payment_pi", label: "Principal & interest" },
              { field: "payment_io", label: "Interest only" },
            ],
          },
          { name: "amortization_years", label: "Amortization (years)", kind: "number", placeholder: "30" },
          { name: "first_payment_date", label: "First payment due", placeholder: "December 1, 2026" },
          { name: "balloon_months", label: "Balloon due (months)", kind: "number" },
          { name: "late_fee", label: "Late fee", kind: "number" },
          { name: "late_days", label: "Late after (days)", kind: "number", placeholder: "10" },
        ],
      },
      {
        title: "Note protections",
        fields: [
          {
            kind: "pickone",
            name: "servicing",
            label: "How payments get made",
            options: [
              { field: "servicing_via_company", label: "Through a servicing company" },
              { field: "servicing_direct", label: "Buyer pays the seller directly" },
            ],
          },
          { name: "servicing_company", label: "Servicing company name", showIf: (f) => f.servicing === "servicing_via_company" },
          {
            kind: "pickone",
            name: "lender_policy",
            label: "Lender's title policy",
            options: [
              { field: "lender_policy_seller", label: "Seller pays" },
              { field: "lender_policy_buyer", label: "Buyer pays" },
            ],
          },
          {
            kind: "pickone",
            name: "note_recourse",
            label: "Note recourse",
            options: [
              { field: "note_nonrecourse", label: "Non-recourse" },
              { field: "note_recourse", label: "Recourse" },
            ],
          },
          {
            kind: "pickone",
            name: "note_transfer",
            label: "If the buyer sells",
            options: [
              { field: "note_assumable", label: "Note is assumable" },
              { field: "note_due_on_sale", label: "Due on sale" },
            ],
          },
        ],
      },
      escrowSection,
      partiesSection,
      costSplits(),
      {
        title: "Extras",
        fields: [
          { name: "section_1_13", label: "Personal property included", kind: "textarea" },
          { name: "section_1_14", label: "Additional terms", kind: "textarea" },
        ],
      },
      signatureSection,
    ],
  },
];

export const getDealType = (id) => DEAL_TYPES.find((t) => t.id === id);

export const visibleFields = (dealType, form) =>
  dealType.sections.flatMap((s) => s.fields).filter((f) => !f.showIf || f.showIf(form));

/** Turn form state into the { field_name: value } object fillContract expects. */
export function toPdfData(dealType, form) {
  const data = {};
  for (const f of visibleFields(dealType, form)) {
    if (f.kind === "pickone") {
      if (form[f.name]) data[form[f.name]] = true;
    } else {
      const v = (form[f.name] ?? "").toString().trim();
      if (!v) continue;
      data[f.name] = f.kind === "date" ? formatContractDate(v, f.dateStyle) : v;
    }
  }
  return data;
}

/** PDFs want the date written out: "November 20" or "November 20, 2026". */
export function formatContractDate(iso, style) {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const month = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
  return style === "long" ? `${month} ${d}, ${y}` : `${month} ${d}`;
}

/** Strip anything that isn't a digit, comma, period or minus — no $ or % allowed. */
export const sanitizeNumber = (v) => v.replace(/[^0-9.,-]/g, "");
